import { useEffect, useRef } from 'react';

/**
 * Intersection Observer hook that adds the 'visible' class to '.reveal' children
 * when they enter the viewport. Uses a MutationObserver to pick up dynamically
 * rendered elements (e.g. conditionally rendered sections after data loads).
 */
const useReveal = (options = {}) => {
  const ref = useRef(null);

  useEffect(() => {
    const el = ref.current;
    if (!el) return;

    const ioOptions = { threshold: 0.05, rootMargin: '0px 0px -20px 0px', ...options };
    const observed = new WeakSet();

    const io = new IntersectionObserver((entries) => {
      entries.forEach((entry) => {
        if (entry.isIntersecting) {
          entry.target.classList.add('visible');
          io.unobserve(entry.target);
        }
      });
    }, ioOptions);

    const observeNew = () => {
      el.querySelectorAll('.reveal:not(.visible)').forEach((t) => {
        if (!observed.has(t)) {
          observed.add(t);
          io.observe(t);
        }
      });
    };

    // Initial pass
    observeNew();

    // Watch for new .reveal elements added to the DOM (e.g. after data fetch)
    const mo = new MutationObserver(observeNew);
    mo.observe(el, { childList: true, subtree: true });

    return () => { io.disconnect(); mo.disconnect(); };
  }, []);

  return ref;
};

export default useReveal;
